import { getScoreColor } from '../utils/scoring';

export default function CompetitiveKillSheet({ audit }) {
  const competitors = Array.isArray(audit.approvedCompetitors) ? audit.approvedCompetitors : Array.isArray(audit.marketData?.competitors) ? audit.marketData.competitors : [];
  const scores = audit.scores || {};
  const ai = audit.aiResearch || {};

  if (competitors.length === 0) return (
    <div className="card p-6">
      <h3 className="font-display text-lg text-white mb-2">Competitive Kill Sheet</h3>
      <p className="text-xs text-neutral-500">Run Market Research (Steps 1-2) to generate competitive strategies.</p>
    </div>
  );

  // Build strategies for each competitor
  const strategies = competitors.map(comp => {
    const theirWeaknesses = (comp.weaknesses || '').toLowerCase();
    const theirStrengths = (comp.strengths || '').toLowerCase();
    const theirRating = comp.reviewScore || 0;
    const yourRating = ai.guestSentiment?.google?.overallRating || 0;

    const attacks = [];

    // Service gap
    if (theirWeaknesses.includes('service') || theirWeaknesses.includes('slow') || theirWeaknesses.includes('rude') || theirWeaknesses.includes('wait')) {
      attacks.push({ type: 'Service', action: `Their guests complain about service. Lead with yours. "Service that remembers you" in marketing.`, priority: 'High' });
    }

    // Price opportunity
    if (theirWeaknesses.includes('price') || theirWeaknesses.includes('expensive') || theirWeaknesses.includes('overpriced')) {
      attacks.push({ type: 'Value', action: `They're seen as overpriced. Position your value — same quality, better experience, smarter pricing.`, priority: 'High' });
    }

    // Food quality
    if (theirWeaknesses.includes('food') || theirWeaknesses.includes('quality') || theirWeaknesses.includes('bland') || theirWeaknesses.includes('inconsistent')) {
      attacks.push({ type: 'Quality', action: `Their food gets complaints. Double down on your kitchen consistency. Showcase chef, sourcing, seasonal.`, priority: 'High' });
    }

    // Ambiance
    if (theirWeaknesses.includes('noise') || theirWeaknesses.includes('loud') || theirWeaknesses.includes('dark') || theirWeaknesses.includes('cramped')) {
      attacks.push({ type: 'Atmosphere', action: `Their space has issues. Highlight your ambiance in content and review responses.`, priority: 'Medium' });
    }

    // Rating gap
    if (yourRating > theirRating && theirRating > 0) {
      attacks.push({ type: 'Reviews', action: `You outrank them ${yourRating} vs ${theirRating}. Feature "Highest rated in [market]" in your marketing.`, priority: 'High' });
    }

    // If they're strong somewhere, acknowledge and pivot
    if (theirStrengths.includes('cocktail') || theirStrengths.includes('bar') || theirStrengths.includes('drink')) {
      attacks.push({ type: 'Differentiate', action: `They own the cocktail narrative. Don't compete head-on — own a different moment (the food, the experience, the private events).`, priority: 'Medium' });
    }

    // Generic strategies if nothing specific
    if (attacks.length === 0) {
      attacks.push({ type: 'General', action: `Differentiate on experience, not just product. Create moments they can't replicate.`, priority: 'Medium' });
    }

    return { ...comp, attacks };
  });

  return (
    <div className="card p-6">
      <h3 className="font-display text-lg text-white mb-1">Competitive Kill Sheet</h3>
      <p className="text-xs text-neutral-500 mb-5">How to beat each competitor — their weakness is your opportunity</p>

      <div className="space-y-4">
        {strategies.map((comp, i) => (
          <div key={i} className="bg-neutral-800/20 border border-neutral-800 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-semibold text-white">{comp.name}</p>
                <p className="text-[10px] text-neutral-500">{comp.type}{comp.reviewScore ? ` · ${comp.reviewScore}★` : ''}</p>
              </div>
            </div>

            {comp.strengths && (
              <p className="text-xs text-emerald-400/70 mb-1">+ Their strength: {comp.strengths}</p>
            )}
            {comp.weaknesses && (
              <p className="text-xs text-red-400/70 mb-3">- Their weakness: {comp.weaknesses}</p>
            )}

            <div className="space-y-2">
              {comp.attacks.map((attack, j) => (
                <div key={j} className="flex items-start gap-2">
                  <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium mt-0.5 ${attack.priority === 'High' ? 'bg-red-400/20 text-red-400' : 'bg-amber-400/20 text-amber-400'}`}>{attack.priority}</span>
                  <div>
                    <span className="text-[10px] text-brand-gold font-semibold">{attack.type}: </span>
                    <span className="text-[10px] text-neutral-400">{attack.action}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
